import { useEffect, useState } from "react";
import { formatDateString, PeriodFilter } from "./general";
import { useQuery } from "react-query";
import apiService from "../service/apiService";
import { apiConfig } from "../config/apiConfig";

type StatisticsCharData = {
  date: Date;
  value: number;
};

type Driver = {
  user_id: string;
  fullName: string;
  profile_picture: string | null;
  rating: string;
  total_revenue: string;
  total_rides: string;
};

type TopDriversResponse = {
  drivers: Driver[];
  count: {
    totalApprovedDrivers: string;
    averageRating: string;
  };
};

function processTopDriversChartData(res: TopDriversResponse) {
  return {
    labels: res.drivers.map((d) => d.fullName),
    datasets: [
      {
        label: "Courses",
        data: res.drivers.map((d) => Number(d.total_rides)),
        backgroundColor: "#22aaff",
        yAxisID: "y",
      },
      {
        label: "Montant total (MAD)",
        data: res.drivers.map((d) => Number(d.total_revenue)),
        backgroundColor: "#f59e42",
        yAxisID: "y1",
      },
    ],
  };
}

export const useTopDriversChart = (
  currentFilter: { startDate: Date; endDate: Date },
  token: string,
) => {
  return useQuery(
    ["getTopDrivers", currentFilter],
    async () => {
      const res: TopDriversResponse = await apiService.MakeGetRequest(
        apiConfig.taxiStatistics.topDrivers({
          startDate: currentFilter.startDate.toISOString().split("T")[0],
          endDate: currentFilter.endDate.toISOString().split("T")[0],
        }),
        token,
      );
      return processTopDriversChartData(res);
    },
    { enabled: !!token },
  );
};

const currentDate = new Date().toISOString().split("T")[0];

const formatYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const firstDayOfMonth = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}-01`;
};

const getWeekDates = (startDate: Date): Date[] => {
  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    dates.push(date);
  }
  return dates;
};

const updateCharData = (
  ridesStatistics: TaxiStatistics,
  filter: PeriodFilter,
  getData: (item: TaxiStatisticBreakdown | null) => number,
): StatisticsCharData[] => {
  let result: StatisticsCharData[] = [];

  const periodDates =
    filter.id === "day"
      ? getWeekDates(filter.startDate)
      : filter.getPeriodDates();

  if (filter.id === "year") {
    const data: { [key: string]: number } = {};
    for (const [key, value] of Object.entries(ridesStatistics.ridesBreakDown)) {
      const itemMonth = firstDayOfMonth(new Date(key));
      if (data[itemMonth]) {
        data[itemMonth] += getData(value);
      } else {
        data[itemMonth] = getData(value);
      }
    }
    const periodData: StatisticsCharData[] = [];
    for (const item of periodDates) {
      const itemMonth = firstDayOfMonth(item);
      periodData.push({
        date: new Date(itemMonth),
        value: data[itemMonth] ?? 0,
      });
    }
    result = periodData;
  } else {
    result = periodDates.map((date) => ({
      date,
      value: getData(
        ridesStatistics.ridesBreakDown[formatYYYMMDD(date)] || null,
      ),
    }));
  }

  return result;
};

const useChartData = (currentFilter: PeriodFilter, token: string) => {
  const [revenueData, setRevenueData] = useState<any>(null);
  const [commissionData, setCommissionData] = useState<any>(null);
  const [ridesData, setRidesData] = useState<any>(null);
  const [driversData, setDriversData] = useState<any>(null);
  const [AllDriversData, setAllDriversData] = useState<any>(null);

  const taxiStatistics = useQuery<TaxiStatistics>(
    ["getTaxiStatistics", currentFilter],
    () => {
      return apiService.MakeGetRequest(
        apiConfig.taxiStatistics.all({
          startDate: currentFilter.startDate.toISOString().split("T")[0],
          endDate: currentFilter.endDate.toISOString().split("T")[0],
        }),
        token,
      );
    },
  );

  const topDriversQuery = useQuery(
    ["getTopDrivers", currentFilter],
    async () => {
      const res: TopDriversResponse = await apiService.MakeGetRequest(
        apiConfig.taxiStatistics.topDrivers({
          limit: 8,
          // startDate: currentFilter.startDate.toISOString().split("T")[0],
          // endDate: currentFilter.endDate.toISOString().split("T")[0]
        }),
        token,
      );
      return res;
    },
    { enabled: !!token },
  );

  const AllDriversQuery = useQuery(
    ["getTopDrivers", currentFilter],
    async () => {
      const res: TopDriversResponse = await apiService.MakeGetRequest(
        apiConfig.taxiStatistics.topDrivers({
          limit: 8,
          // startDate: currentFilter.startDate.toISOString().split("T")[0],
          // endDate: currentFilter.endDate.toISOString().split("T")[0]
          page: 1,
        }),
        token,
      );
      return res;
    },
    { enabled: !!token },
  );

  useEffect(() => {
    if (taxiStatistics.data) {
      const revenueChartData = updateCharData(
        taxiStatistics.data,
        currentFilter,
        (item) => item?.revenue ?? 0,
      );

      const commissionChartData = updateCharData(
        taxiStatistics.data,
        currentFilter,
        (item) => item?.commission ?? 0,
      );

      const ridesChartData = updateCharData(
        taxiStatistics.data,
        currentFilter,
        (item) => item?.count ?? 0,
      );

      const filteredRevenueData = revenueChartData.filter((item) => {
        const itemDate = formatYYYMMDD(item.date);
        return itemDate <= currentDate;
      });
      const filteredCommissionData = commissionChartData.filter((item) => {
        const itemDate = formatYYYMMDD(item.date);
        return itemDate <= currentDate;
      });

      const processedRevenueData = {
        labels: revenueChartData.map((item) =>
          formatDateString(formatYYYMMDD(item.date)),
        ),
        datasets: [
          {
            label: "Revenus",
            data: filteredRevenueData.map((item) => item.value.toFixed(0)),
            borderColor: "#22aaff",
            backgroundColor: "#22aaff",
            tension: 0.4,
            fill: true,
          },
        ],
      };

      const processedCommissionData = {
        labels: commissionChartData.map((item) =>
          formatDateString(formatYYYMMDD(item.date)),
        ),
        datasets: [
          {
            label: "Commission",
            data: filteredCommissionData.map((item) => item.value.toFixed(1)),
            borderColor: "#22aaff",
            backgroundColor: "#22aaff",
            tension: 0.4,
            fill: true,
          },
        ],
      };

      const processedRidesData = {
        labels: ridesChartData.map((item) =>
          formatDateString(formatYYYMMDD(item.date)),
        ),
        datasets: [
          {
            label: "Rides",
            data: ridesChartData.map((item) => item.value),
            borderColor: "#22aaff",
            backgroundColor: "#ff7722",
            tension: 0.4,
            fill: true,
          },
        ],
      };

      setRevenueData(processedRevenueData);
      setCommissionData(processedCommissionData);
      setRidesData(processedRidesData);
    }

    if (topDriversQuery.data) {
      setDriversData({
        drivers: topDriversQuery.data.drivers,
      });
    }

    if (AllDriversQuery.data) {
      setAllDriversData({
        drivers: AllDriversQuery.data,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxiStatistics.data, topDriversQuery.data, currentFilter]);

  const drivers = driversData?.drivers || [];

  const chartData = {
    labels: drivers.map((d: { fullName: any }) => d.fullName),
    datasets: [
      {
        label: "Revenu",
        data: drivers.map((d: Driver) => Number(d.total_revenue)),
        backgroundColor: drivers.map(
          (_: any, i: number) =>
            [
              "#22aaff",
              "#f59e42",
              "#74b9ff",
              "#ff9a56",
              "#6c5ce7",
              "#00b894",
              "#fd79a8",
            ][i % 7],
        ),
        borderRadius: 16,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  return {
    taxiStatistics,
    topDriversQuery,
    revenueData,
    commissionData,
    ridesData,
    driversData,
    AllDriversData,
    chartData,
    isLoading: taxiStatistics.isLoading || topDriversQuery.isLoading,
    error: taxiStatistics.error || topDriversQuery.error,
  };
};

export { useChartData, updateCharData };
